import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { COMPANY, callUrl } from "@/lib/config";
import { cn } from "@/lib/utils";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_shifting-kerala/artifacts/zqze6kl6_done%20%2810%29.png";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/service-areas", label: "Service Areas" },
  { to: "/gallery", label: "Gallery" },
  { to: "/reviews", label: "Reviews" },
  { to: "/blog", label: "Blog" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={cn(
        "fixed top-0 inset-x-0 z-50 transition-all",
        scrolled ? "bg-white/95 backdrop-blur-xl border-b border-slate-200 shadow-sm" : "bg-white/80 backdrop-blur-md border-b border-transparent"
      )}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center group">
          <div
            className="h-14 w-44 sm:h-16 sm:w-52 bg-no-repeat bg-center group-hover:scale-[1.03] transition"
            style={{ backgroundImage: `url(${LOGO_URL})`, backgroundSize: "200%", backgroundPosition: "center" }}
            aria-label={COMPANY.name}
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                cn(
                  "text-sm font-medium transition-colors",
                  isActive ? "text-[#081537]" : "text-slate-600 hover:text-[#081537]"
                )
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href={callUrl}
            data-testid="header-call-btn"
            className="flex items-center gap-2 text-sm font-semibold text-[#081537] hover:text-[#081537] transition"
          >
            <Phone className="w-4 h-4" /> {COMPANY.phone}
          </a>
          <Link
            to="/book"
            data-testid="header-book-btn"
            className="bg-[#081537] hover:bg-[#0F2152] text-white rounded-full px-5 py-2.5 text-sm font-semibold transition-all hover:-translate-y-0.5 shadow-md shadow-blue-900/20"
          >
            Request Private Consultation
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden w-10 h-10 grid place-items-center rounded-lg text-[#081537]"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-slate-100 bg-white" data-testid="mobile-menu">
          <div className="px-4 py-4 flex flex-col gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  cn(
                    "px-3 py-3 rounded-lg text-base font-medium",
                    isActive ? "bg-slate-100 text-[#081537]" : "text-slate-700 hover:bg-slate-50"
                  )
                }
              >
                {n.label}
              </NavLink>
            ))}
            <Link
              to="/book"
              onClick={() => setOpen(false)}
              data-testid="mobile-book-btn"
              className="mt-2 bg-[#081537] text-white rounded-full px-5 py-3 text-center font-semibold"
            >
              Request Private Consultation
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
